import {
  type CustomerInfo,
  type Offerings,
  Purchases,
} from "@revenuecat/purchases-js";
import {
  createContext,
  type FunctionComponent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import config from "../../config";
import { useAuthContext } from "../auth/AuthContext";

type PurchasesContextType = {
  isConfigured: boolean;
  isLoading: boolean;
  customerInfo: CustomerInfo | null;
  offerings: Offerings | null;
  isPremium: boolean;
  subscriptionExpirationDate: string | null;
  refreshCustomerInfo: () => Promise<void>;
  getPurchaseLink: () => string | null;
  getCustomerPortalLink: () => string | null;
};

const ANONYMOUS_USER_ID_KEY = "microbeta_anonymous_rc_id";

const getAnonymousAppUserId = (): string => {
  let id = localStorage.getItem(ANONYMOUS_USER_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ANONYMOUS_USER_ID_KEY, id);
  }
  return id;
};

const defaultPurchasesContext: PurchasesContextType = {
  isConfigured: false,
  isLoading: true,
  customerInfo: null,
  offerings: null,
  isPremium: false,
  subscriptionExpirationDate: null,
  refreshCustomerInfo: async () => {},
  getPurchaseLink: () => null,
  getCustomerPortalLink: () => null,
};

const PurchasesContext = createContext<PurchasesContextType>(
  defaultPurchasesContext
);

export const PurchasesProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { authState, authIsPending } = useAuthContext();
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const configurationAttempted = useRef(false);

  // Initialize RevenueCat with identified customers
  // This allows cross-platform subscription sharing (web and mobile)
  // Wait for auth to be ready before initializing to ensure userId is available
  useEffect(() => {
    if (configurationAttempted.current || authIsPending) {
      return;
    }

    if (configurationAttempted.current || authIsPending) {
      return;
    }

    configurationAttempted.current = true;

    const initializeRevenueCat = async () => {
      try {
        // Only initialize if feature flag is enabled
        if (!config.FEATURE_FLAG_SHOW_PAYMENTS) {
          setIsLoading(false);
          return;
        }

        const apiKey = config.VITE_PUBLIC_REVENUE_CAT_API_KEY;

        if (!apiKey) {
          console.error(
            "[PurchasesContext] VITE_PUBLIC_REVENUE_CAT_API_KEY is not set"
          );
          setIsLoading(false);
          return;
        }

        // Get App User ID from authentication system (AuthContext) or use anonymous ID
        // This links the customer's account for cross-platform subscription access
        const appUserId = authState?.userId || getAnonymousAppUserId();

        // Configure RevenueCat with API key and App User ID
        const purchases = Purchases.configure({
          apiKey,
          appUserId,
        });

        setIsConfigured(true);

        // Fetch initial customer info and offerings
        try {
          const info = await purchases.getCustomerInfo();
          setCustomerInfo(info);

          const currentOfferings = await purchases.getOfferings();
          setOfferings(currentOfferings);
        } catch (infoError) {
          console.warn(
            "[PurchasesContext] Failed to fetch initial customer info:",
            infoError
          );
        }
      } catch (error) {
        console.error(
          "[PurchasesContext] Failed to initialize RevenueCat:",
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    initializeRevenueCat();
    // Wait for auth to be ready before initializing - user identity changes are handled in separate effect below
  }, [authState?.userId, authIsPending]);

  // Handle user identity changes (login/logout)
  // When user logs in/out, we need to identify or reset the customer
  useEffect(() => {
    if (!config.FEATURE_FLAG_SHOW_PAYMENTS || !isConfigured) return;

    const handleUserChange = async () => {
      const appUserId = authState?.userId;

      try {
        const purchases = Purchases.getSharedInstance();
        if (appUserId) {
          // Change the user to the authenticated appUserId
          // This links their subscription across platforms (web and mobile)
          const info = await purchases.changeUser(appUserId);
          setCustomerInfo(info);
          const currentOfferings = await purchases.getOfferings();
          setOfferings(currentOfferings);
        } else {
          // When user logs out, we identify as anonymous
          await purchases.changeUser(getAnonymousAppUserId());
          const currentOfferings = await purchases.getOfferings();
          setOfferings(currentOfferings);
          setCustomerInfo(null);
          // Do not reset configuration status so we can still fetch pricing
        }
      } catch (error) {
        console.error(
          "[PurchasesContext] Failed to handle user identity change:",
          error
        );
      }
    };

    handleUserChange();
  }, [authState?.userId, isConfigured]);

  // Check if user has premium access
  // Entitlement ID matches mobile: "microbeta-premium"
  const isPremium =
    customerInfo?.entitlements.active["microbeta-premium"] !== undefined;

  // Get subscription expiration date (convert Date to ISO string)
  const expirationDate =
    customerInfo?.entitlements.active["microbeta-premium"]?.expirationDate;
  const subscriptionExpirationDate =
    expirationDate instanceof Date
      ? expirationDate.toISOString()
      : typeof expirationDate === "string"
        ? expirationDate
        : null;

  // Refresh customer info
  const refreshCustomerInfo = useCallback(async () => {
    if (!config.FEATURE_FLAG_SHOW_PAYMENTS || !isConfigured) {
      return;
    }

    try {
      const purchases = Purchases.getSharedInstance();
      const info = await purchases.getCustomerInfo();
      setCustomerInfo(info);
    } catch (error) {
      console.error(
        "[PurchasesContext] Failed to refresh customer info:",
        error
      );
    }
  }, [isConfigured]);

  // Get RevenueCat Web Purchase Link URL
  // For identified customers, append appUserId as a path segment to link the purchase to their account
  // Format: https://pay.rev.cat/{linkId}/{appUserId}
  const getPurchaseLink = useCallback((): string | null => {
    if (!config.FEATURE_FLAG_SHOW_PAYMENTS) {
      return null;
    }

    const purchaseLinkUrl = config.VITE_REVENUECAT_PURCHASE_LINK_URL;

    if (!purchaseLinkUrl) {
      console.warn(
        "[PurchasesContext] VITE_REVENUECAT_PURCHASE_LINK_URL is not configured"
      );
      return null;
    }

    // For identified customers, append appUserId as a path segment
    // Example: https://pay.rev.cat/dwtgxmrfbcvvkvmo/appUserId
    const appUserId = authState?.userId;
    if (appUserId) {
      // Ensure URL ends with / before appending userId
      const baseUrl = purchaseLinkUrl.endsWith("/")
        ? purchaseLinkUrl
        : `${purchaseLinkUrl}/`;
      return `${baseUrl}${appUserId}`;
    }

    return purchaseLinkUrl;
  }, [authState?.userId]);

  // Get RevenueCat Customer Portal URL for managing subscriptions
  // Uses managementURL from CustomerInfo (provided by RevenueCat for the active subscription)
  const getCustomerPortalLink = useCallback((): string | null => {
    if (!config.FEATURE_FLAG_SHOW_PAYMENTS) {
      return null;
    }

    if (!customerInfo?.managementURL) {
      console.warn(
        "[PurchasesContext] managementURL not available in customerInfo"
      );
      return null;
    }

    return customerInfo.managementURL;
  }, [customerInfo]);

  return (
    <PurchasesContext.Provider
      value={{
        isConfigured,
        isLoading,
        customerInfo,
        offerings,
        isPremium,
        subscriptionExpirationDate,
        refreshCustomerInfo,
        getPurchaseLink,
        getCustomerPortalLink,
      }}
    >
      {children}
    </PurchasesContext.Provider>
  );
};

export const usePurchases = (): PurchasesContextType => {
  const context = useContext(PurchasesContext);
  if (context === undefined) {
    throw new Error("usePurchases must be used within a PurchasesProvider");
  }
  return context;
};
